import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import type { PermissionAction } from '@/api/domains/authorization/permission/permissions.types';


interface PermissionGuardProps {
  /** Route code to check. If omitted, auto-resolves from current URL. */
  screenCode?: string;
  /** Action to check (e.g., 'create', 'edit', 'delete'). */
  action: PermissionAction;
  /** Behavior when permission is denied. Default: 'disabled'. */
  fallback?: 'hidden' | 'disabled';
  /** Force the denied state regardless of the permission check
   * (useful for employee-level permissions). */
  denied?: boolean;
  children: React.ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  screenCode,
  action,
  fallback = 'disabled',
  denied = false,
  children,
}) => {
  const { t: translate } = useTranslation();
  const { can, isLoaded } = usePagePermissions(screenCode);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  if (!isLoaded) return null;

  const allowed = can(action) && !denied;
  if (allowed) {
    return <>{children}</>;
  }

  if (fallback === 'disabled') {
    return (
      <div
        ref={wrapperRef}
        className="relative inline-block w-fit"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div
          className="pointer-events-none opacity-50"
          aria-disabled="true"
          inert
        >
          {children}
        </div>
        {showTooltip && (
          <Tooltip
            body={translate('common:permissions.denied')}
            anchorRef={wrapperRef}
            placement="top"
          />
        )}
      </div>
    );
  }

  // fallback === 'hidden'
  return null;
};
